import React, { useState } from "react";
import { useFetch } from "@/lib/api";
import { PageHeader, StatusChip, ConfidenceBadge, LoadingState, OutcomeChip, DemoBadge } from "@/components/app/primitives";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const HEAT = { 1: "var(--heat-1)", 2: "var(--heat-2)", 3: "var(--heat-3)", 4: "var(--heat-4)", 5: "var(--heat-5)" };
const RATING_LABELS = { 1: "Weak", 2: "Emerging", 3: "Defined", 4: "Managed", 5: "Leading" };

export default function MatrixPage() {
  const { data: rows, loading } = useFetch("/matrix");
  const [selected, setSelected] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [confFilter, setConfFilter] = useState("all");

  if (loading) return <LoadingState message="Scoring dimensions..." />;

  const filtered = (rows || []).filter((r) =>
    (groupFilter === "all" || r.group === groupFilter) &&
    (confFilter === "all" || r.confidence === confFilter)
  );

  return (
    <div data-testid="matrix-page">
      <PageHeader
        kicker="Scene 4 - Dynamic assessment matrix"
        title="Assessment Matrix"
        subtitle="SIX Program Leadership Process and operating dimensions, scored 1-5 with evidence strength, gaps, implications, and owner-dated actions. Click any row for the full record."
        right={<DemoBadge />}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "Program Leadership", "Operating Dimensions"].map((g) => (
          <button key={g} onClick={() => setGroupFilter(g)} data-testid={`matrix-filter-group-${g.replace(/\s/g, "-").toLowerCase()}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${groupFilter === g ? "border-[--six-crimson] bg-[--six-crimson-soft] text-[--tx-1]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
            {g === "all" ? "All dimensions" : g}
          </button>
        ))}
        <div className="h-5 w-px bg-[--divider]" />
        {["all", "Evidence-backed", "Hypothesis"].map((c) => (
          <button key={c} onClick={() => setConfFilter(c)} data-testid={`matrix-filter-conf-${c.toLowerCase()}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${confFilter === c ? "border-[--ai] bg-[--ai-soft] text-[--tx-1]" : "border-[--hairline] bg-[--panel-dark] text-[--tx-2] hover:text-[--tx-1]"}`}>
            {c === "all" ? "Any confidence" : c}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          {Object.entries(RATING_LABELS).map(([n, l]) => (
            <span key={n} className="flex items-center gap-1 text-[10px] text-[--tx-3]">
              <span className="w-3 h-3 rounded border border-white/10" style={{ background: HEAT[n] }} /> {n}={l}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-left dense-table" data-testid="matrix-table">
            <thead className="bg-[--shell-2]">
              <tr className="border-b border-[--divider]">
                <th className="py-2.5 px-3">Dimension</th>
                <th className="py-2.5 px-3">Rating</th>
                <th className="py-2.5 px-3 hidden lg:table-cell">Key gap</th>
                <th className="py-2.5 px-3">Confidence</th>
                <th className="py-2.5 px-3 hidden md:table-cell">Owner needed</th>
                <th className="py-2.5 px-3 hidden xl:table-cell">Timing</th>
                <th className="py-2.5 px-3">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} onClick={() => setSelected(row)} data-testid={`matrix-row-${row.id}`}
                  className="border-b border-[--divider] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors">
                  <td className="py-2 px-3">
                    <div className="text-[13px] font-medium text-[--tx-1]">{row.dimension}</div>
                    <div className="text-[10px] text-[--tx-3]">{row.group}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span key={n} className="w-6 h-5 rounded-sm border border-white/10 flex items-center justify-center text-[9px] font-mono"
                          style={{ background: n <= row.rating ? HEAT[row.rating] : "var(--shell-2)", color: n <= row.rating ? "#E9EEF5" : "var(--tx-3)" }}>
                          {n === row.rating ? n : ""}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-3 hidden lg:table-cell text-xs text-[--tx-2] max-w-[300px]"><span className="line-clamp-2">{row.key_gaps}</span></td>
                  <td className="py-2 px-3"><ConfidenceBadge level={row.confidence} /></td>
                  <td className="py-2 px-3 hidden md:table-cell text-xs text-[--tx-2]">{row.owner_needed}</td>
                  <td className="py-2 px-3 hidden xl:table-cell text-xs font-mono text-[--tx-3]">{row.timing}</td>
                  <td className="py-2 px-3"><OutcomeChip outcome={row.six_outcome} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[--shell-2] border-[--hairline] w-full sm:max-w-md overflow-y-auto thin-scroll" data-testid="matrix-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[--tx-1] font-display">{selected.dimension}</SheetTitle>
                <SheetDescription className="text-[--tx-3] text-xs">{selected.group} · Rating {selected.rating}/5 ({RATING_LABELS[selected.rating]}) · Horizon {selected.horizon}</SheetDescription>
              </SheetHeader>
              <div className="space-y-3.5 mt-4">
                {[
                  ["Known facts", selected.known_facts], ["Key gaps", selected.key_gaps],
                  ["Business implication", selected.business_implication], ["Recommended action", selected.recommended_action],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">{label}</div>
                    <p className="text-[13px] text-[--tx-2] leading-relaxed">{val}</p>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[--tx-3] mb-0.5">Owner needed</div><div className="text-[--tx-1] font-medium">{selected.owner_needed}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Timing</div><div className="text-[--tx-1] font-medium">{selected.timing}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Evidence strength</div><div className="text-[--tx-1] font-medium">{selected.evidence_strength}</div></div>
                  <div><div className="text-[--tx-3] mb-0.5">Related outcome</div><OutcomeChip outcome={selected.six_outcome} /></div>
                </div>
                <div className="flex gap-2 pt-1"><ConfidenceBadge level={selected.confidence} /><StatusChip tone="neutral">{selected.horizon}</StatusChip></div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
